import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDocumentationPost, getVisibleDocumentationPosts } from '../api/documentation';
import { Link, useNavigate } from 'react-router-dom';
import DocumentationPostDialog from '../components/documentation/DocumentationPostDialog';

const Documentation = () => {
  const [documentationPosts, setDocumentationPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedDocId, setCopiedDocId] = useState(null);
  const [fullDocumentationById, setFullDocumentationById] = useState({});
  const { user } = useAuth();
  const isAdmin = user?.is_staff || user?.is_superuser;
  const navigate = useNavigate();

  useEffect(() => {
    fetchDocumentationPosts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPosts(documentationPosts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = documentationPosts.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.content.toLowerCase().includes(query)
      );
      setFilteredPosts(filtered);
    }
  }, [searchQuery, documentationPosts]);

  const fetchDocumentationPosts = async () => {
    try {
      setLoading(true);
      const data = await getVisibleDocumentationPosts();
      const sortedData = [...data].sort((a, b) => a.order_index - b.order_index);
      setDocumentationPosts(sortedData);
      setFilteredPosts(sortedData);
    } catch (err) {
      console.error('Error fetching documentation posts:', err);
      setError('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (post) => {
    setSelectedPost(post);
    setDialogOpen(true);
  };

  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) fetchDocumentationPosts();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const navigateToDocDetail = (post) => {
    if (post.route) {
      navigate(`/documentation/${post.route}`);
    } else {
      navigate(`/documentation/route/${post.id}`);
    }
  };

  const getFullDocumentationPost = async (post) => {
    if (fullDocumentationById[post.id]) {
      return fullDocumentationById[post.id];
    }

    const fullPost = await getDocumentationPost(post.id);
    setFullDocumentationById((prev) => ({
      ...prev,
      [post.id]: fullPost
    }));
    return fullPost;
  };

  const handleCopyDoc = async (post, event) => {
    event.stopPropagation();

    try {
      const fullPost = await getFullDocumentationPost(post);
      const docsPayload = [
        `# ${fullPost.title}`,
        `route: ${fullPost.route || `/documentation/route/${fullPost.id}`}`,
        '',
        fullPost.content.trim()
      ].join('\n');

      await navigator.clipboard.writeText(docsPayload);
      setCopiedDocId(post.id);
      window.setTimeout(() => {
        setCopiedDocId((currentId) => (currentId === post.id ? null : currentId));
      }, 2000);
    } catch (err) {
      console.error('Error copying documentation:', err);
    }
  };

  // Markdown preview cleaner
  const formatPreview = (content, maxLength = 120) => {
    let cleanContent = content
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/~~(.*?)~~/g, '$1')
      .replace(/`{1,3}(.*?)`{1,3}/g, '$1')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
      .replace(/^\s*>\s+/gm, '')
      .replace(/\n+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return cleanContent.length > maxLength
      ? `${cleanContent.substring(0, maxLength)}...`
      : cleanContent;
  };

  return (
    <div
      className="
        box-border           /* include padding in width calc */
        mx-auto w-full
        px-2 sm:px-4 md:px-6 lg:px-8
        max-w-full           /* no 100vw + padding overflow */
        sm:max-w-screen-sm
        md:max-w-screen-md
        lg:max-w-screen-lg
        xl:max-w-screen-xl
        overflow-x-hidden    /* last-resort guard on tiny devices */
      "
    >
      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Documentation
        </h1>
        <p className="text-lg text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
          Learn how to use the Astronomy Data Portal
        </p>
      </div>

      {/* Search + Admin button */}
      <div className="mb-10 flex flex-col sm:flex-row justify-between items-center w-full gap-2 sm:gap-4">
        <div className="relative w-full min-w-0 sm:max-w-md">
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full p-2.5 pl-10 rounded-lg bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary placeholder:text-ui-light-text-tertiary dark:placeholder:text-ui-dark-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 transition"
          />
          <svg
            className="w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary absolute left-3 top-3 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        <div className="flex w-full sm:w-auto gap-2">
          {isAdmin && (
            <Link
              to="/admin/documentation"
              className="inline-block w-full sm:w-auto text-center px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition"
            >
              Manage
            </Link>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          {error}
        </div>
      )}

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center py-16">
          <svg
            className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-label="Loading"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Empty state */}
          {filteredPosts.length === 0 ? (
            <div className="p-6 text-center rounded-lg bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary shadow-sm">
              {documentationPosts.length === 0
                ? 'No documentation content is currently available.'
                : 'No results match your search query.'}
            </div>
          ) : (
            filteredPosts.map(post => (
              <div
                key={post.id}
                onClick={() => navigateToDocDetail(post)}
                className="
                  p-5 rounded-xl bg-ui-light-card dark:bg-ui-dark-card
                  shadow-sm hover:shadow-md transition-all cursor-pointer
                  hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover
                  border border-transparent hover:border-ui-light-border dark:hover:border-ui-dark-border
                  min-w-0 w-full overflow-hidden
                "
              >
                  <div className="flex items-start justify-between gap-2 sm:gap-4 min-w-0">
                  {/* Left: Title & preview */}
                  <div className="flex-1 basis-0 min-w-0 max-w-full">
                    <div className="flex items-center gap-2 mb-1 min-w-0">
                      <svg
                        className="w-4 h-4 flex-shrink-0 text-ui-light-detail dark:text-ui-dark-detail"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>

                      {/* Wrap on xs, clamp to 2 lines; can switch to single-line on sm+ if you prefer */}
                      <h3 className="text-base font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary break-words line-clamp-2 sm:line-clamp-1 sm:truncate">
                        {post.title}
                      </h3>
                    </div>

                    {/* Preview: wrap on xs, clamp to 2 lines */}
                    <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary break-words line-clamp-2 sm:line-clamp-1">
                      {formatPreview(post.content)}
                    </p>
                  </div>

                  {/* Right: Indicators */}
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleCopyDoc(post, e)}
                      className="inline-flex items-center rounded-md border border-ui-light-border dark:border-ui-dark-border px-3 py-1.5 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                      title="Copy this documentation"
                      aria-label="Copy this documentation"
                    >
                      {copiedDocId === post.id ? 'Copied' : 'Copy'}
                    </button>

                    {post.attachments?.length > 0 && (
                      <div className="flex items-center text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                        <span>{post.attachments.length}</span>
                      </div>
                    )}

                    {isAdmin && (
                      <button
                        type="button"
                        className="p-1.5 text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-full transition-colors"
                        onClick={e => {
                          e.stopPropagation();
                          handleEditClick(post);
                        }}
                        title="Edit documentation"
                        aria-label="Edit documentation"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    )}

                    <div className="text-ui-light-detail dark:text-ui-dark-detail">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <DocumentationPostDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        post={selectedPost}
      />
    </div>
  );
};

export default Documentation;
